import { create } from 'zustand';
import type { ActiveFilters } from '@/types';
import { emptyFilters } from '@/types';
import type { GroupBy } from '@/types';

// --- SitePlan ----------------------------------------------------------
export type SitePlanProject = 'VN' | 'VI' | 'VB' | 'VC';

export interface SitePlanFilters {
  estado: string | null;
  modelo: string | null;
  vendedor: string | null;
  // Nuevos
  banco: string | null;
  formalizador: string | null;
  frente: number | null;
  fondo: number | null;
  enConstruccion: boolean;
  conPermiso: boolean;
}

export const emptySitePlanFilters: SitePlanFilters = {
  estado: null,
  modelo: null,
  vendedor: null,
  banco: null,
  formalizador: null,
  frente: null,
  fondo: null,
  enConstruccion: false,
  conPermiso: false,
};
// ----------------------------------------------------------------------

interface AppState {
  groupBy: GroupBy;
  setGroupBy: (g: GroupBy) => void;
  search: string;
  setSearch: (s: string) => void;
  selectedTaskId: number | null;
  selectTask: (id: number | null) => void;
  isCreatingTask: boolean;
  setCreatingTask: (v: boolean) => void;
  // Cambio de estado pendiente disparado por drag-and-drop a una columna que
  // requiere FechaCompletado. El modal lo consume al hidratar para preseleccionar
  // el estado nuevo y forzar que el usuario complete la fecha antes de guardar.
  pendingStatusChange: { taskId: number; newStatusId: number } | null;
  setPendingStatusChange: (change: { taskId: number; newStatusId: number } | null) => void;
  filters: ActiveFilters;
  setFilter: <K extends keyof ActiveFilters>(key: K, value: ActiveFilters[K]) => void;
  resetFilters: () => void;
  filtersOpen: boolean;
  setFiltersOpen: (open: boolean) => void;
  // Drawer izquierdo de navegacion (hamburger en el BottomBar). Mounted
  // siempre; controlado por este flag para que el icono pueda animarse
  // segun el estado abierto/cerrado.
  navMenuOpen: boolean;
  setNavMenuOpen: (open: boolean) => void;

  // SitePlan
  sitePlanProject: SitePlanProject;
  setSitePlanProject: (p: SitePlanProject) => void;
  sitePlanFilters: SitePlanFilters;
  setSitePlanFilter: <K extends keyof SitePlanFilters>(key: K, value: SitePlanFilters[K]) => void;
  resetSitePlanFilters: () => void;
  sitePlanTableOpen: boolean;
  setSitePlanTableOpen: (open: boolean) => void;

  // Tramites (Kanban de lotes). Lote seleccionado para mostrar en el modal
  // de detalle + filtro por creador APC.
  selectedLoteId: number | null;
  selectLote: (id: number | null) => void;
  tramitesCreadorApcId: number | null;
  setTramitesCreadorApcId: (id: number | null) => void;

  // Feed (Actividad del equipo): filtros de la pagina. Local al feed
  // — no se sincroniza con filters globales del kanban/tabla.
  feedIdColFilter: number | null;
  setFeedIdColFilter: (id: number | null) => void;
  feedTipoFilter: string | null; // 'creacion' | 'comentario' | 'cambio' | null
  setFeedTipoFilter: (tipo: string | null) => void;

  // Modo Planificación de sprint: arma el próximo sprint (viernes -> lunes).
  // planningMode togglea el tablero de planning; planningSprintId es el sprint
  // objetivo que se está armando (default = semanal del próximo lunes).
  planningMode: boolean;
  setPlanningMode: (v: boolean) => void;
  planningSprintId: number | null;
  setPlanningSprintId: (id: number | null) => void;

  // Pantalla completa: la enciende una vista que necesita TODO el alto y se
  // dibuja su propia cabecera. Mientras esté encendida, el shell deja de pintar
  // TopBar y BottomBar. Hoy la usa el editor de planta (pedido de Daniel del
  // 19-oct-2026: las dos franjas se repiten en toda la app y adentro del editor
  // no aportan nada, pero le comen 112 px de 844 en el celular).
  //
  // Es una bandera y no una regla por ruta a propósito: `/planta` tiene DOS
  // estados —la lista para elegir el modelo y el editor abierto— y sólo el
  // segundo va a pantalla completa. Matar la navegación también en la lista
  // dejaría esa pantalla sin salida.
  pantallaCompleta: boolean;
  setPantallaCompleta: (v: boolean) => void;
}

export const useAppStore = create<AppState>((set) => ({
  groupBy: 'status',
  setGroupBy: (g) => set({ groupBy: g }),
  search: '',
  setSearch: (s) => set({ search: s }),
  selectedTaskId: null,
  selectTask: (id) => set({ selectedTaskId: id }),
  isCreatingTask: false,
  setCreatingTask: (v) => set({ isCreatingTask: v }),
  pendingStatusChange: null,
  setPendingStatusChange: (change) => set({ pendingStatusChange: change }),
  filters: { ...emptyFilters },
  setFilter: (key, value) =>
    set((state) => ({ filters: { ...state.filters, [key]: value } })),
  resetFilters: () => set({ filters: { ...emptyFilters } }),
  filtersOpen: false,
  setFiltersOpen: (open) => set({ filtersOpen: open }),
  navMenuOpen: false,
  setNavMenuOpen: (open) => set({ navMenuOpen: open }),

  // SitePlan
  sitePlanProject: 'VN',
  setSitePlanProject: (p) => set({ sitePlanProject: p }),
  sitePlanFilters: { ...emptySitePlanFilters },
  setSitePlanFilter: (key, value) =>
    set((state) => ({ sitePlanFilters: { ...state.sitePlanFilters, [key]: value } })),
  resetSitePlanFilters: () => set({ sitePlanFilters: { ...emptySitePlanFilters } }),
  sitePlanTableOpen: false,
  setSitePlanTableOpen: (open) => set({ sitePlanTableOpen: open }),

  // Tramites
  selectedLoteId: null,
  selectLote: (id) => set({ selectedLoteId: id }),
  tramitesCreadorApcId: null,
  setTramitesCreadorApcId: (id) => set({ tramitesCreadorApcId: id }),

  // Feed
  feedIdColFilter: null,
  setFeedIdColFilter: (id) => set({ feedIdColFilter: id }),
  feedTipoFilter: null,
  setFeedTipoFilter: (tipo) => set({ feedTipoFilter: tipo }),

  // Planning
  planningMode: false,
  setPlanningMode: (v) => set({ planningMode: v }),
  planningSprintId: null,
  setPlanningSprintId: (id) => set({ planningSprintId: id }),

  pantallaCompleta: false,
  setPantallaCompleta: (v) => set({ pantallaCompleta: v }),
}));